import { useState } from 'react';
import { Mic, Upload } from 'lucide-react';
import {
  MEETING_TYPES,
  requestMeetingNoteUploadUrl,
  uploadToSignedUrl,
  type MeetingNoteOwner,
} from '../../lib/meetingNotesApi';
import { useCreateMeetingNote, useIngestMeetingNote } from '../../hooks/useMeetingNotes';
import { useUploadQueue } from '../../hooks/useUploadQueue';
import { useI18n } from '../../contexts/I18nContext';
import { Select } from '../ui/Select';
import { UploadProgressList } from '../ui/UploadProgress';
import { DateField } from '../ui/DateField';

function meetingTypeLabel(t: string): string {
  return t.charAt(0) + t.slice(1).toLowerCase();
}

export function MeetingNoteForms({
  owner,
  kind,
  onDone,
}: {
  owner: MeetingNoteOwner;
  kind: 'manual' | 'ingest';
  onDone: () => void;
}) {
  return kind === 'manual' ? (
    <ManualForm owner={owner} onDone={onDone} />
  ) : (
    <IngestForm owner={owner} onDone={onDone} />
  );
}

function ManualForm({ owner, onDone }: { owner: MeetingNoteOwner; onDone: () => void }) {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [attendees, setAttendees] = useState('');
  const [meetingType, setMeetingType] = useState<string>('GENERAL');
  const [content, setContent] = useState('');
  const createMutation = useCreateMeetingNote(owner);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !content.trim()) return;
    await createMutation.mutateAsync({
      title: title.trim(),
      date: date || undefined,
      attendees: attendees.trim() || undefined,
      meetingType,
      content: content.trim(),
    });
    onDone();
  }

  return (
    <form
      onSubmit={submit}
      className="mb-4 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4"
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <input
          placeholder="Titlu *"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none sm:col-span-2"
        />
        <DateField value={date} onChange={setDate} />
        <Select
          label="Tip întâlnire"
          hideLabel
          value={meetingType}
          onChange={setMeetingType}
          options={MEETING_TYPES.map((t) => ({ value: t, label: meetingTypeLabel(t) }))}
        />
      </div>
      <input
        placeholder="Participanți"
        value={attendees}
        onChange={(e) => setAttendees(e.target.value)}
        className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
      />
      <textarea
        placeholder="Conținut (markdown) *"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={5}
        className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onDone}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          Anulează
        </button>
        <button
          type="submit"
          disabled={createMutation.isPending}
          className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
        >
          Salvează
        </button>
      </div>
    </form>
  );
}

function IngestForm({ owner, onDone }: { owner: MeetingNoteOwner; onDone: () => void }) {
  const [text, setText] = useState('');
  const [title, setTitle] = useState('');
  const [meetingType, setMeetingType] = useState<string>('GENERAL');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ingestMutation = useIngestMeetingNote(owner);
  const { t } = useI18n();
  // A meeting recording is the largest upload in the app — tens of MB, then a
  // Whisper transcription on top. Both waits get shown, in that order.
  const { uploads, run } = useUploadQueue();
  const [processingLabel, setProcessingLabel] = useState<string>('');

  async function submitText(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!text.trim()) {
      setError('Introdu textul notiței.');
      return;
    }
    setBusy(true);
    try {
      await ingestMutation.mutateAsync({
        text: text.trim(),
        title: title.trim() || undefined,
        meetingType,
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Procesarea a eșuat.');
    } finally {
      setBusy(false);
    }
  }

  async function handlePdf(file: File) {
    setError(null);
    setBusy(true);
    setProcessingLabel(t('upload.processingExtract'));
    try {
      await run(file, async (onProgress) => {
        const contentType = file.type || 'application/pdf';
        const { uploadUrl, key } = await requestMeetingNoteUploadUrl(owner, {
          fileName: file.name,
          contentType,
        });
        await uploadToSignedUrl(uploadUrl, file, contentType, onProgress);
        await ingestMutation.mutateAsync({
          s3Key: key,
          title: title.trim() || file.name,
          meetingType,
        });
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Încărcarea a eșuat.');
    } finally {
      setBusy(false);
    }
  }

  // Audio recording: upload then ingest via `audioKey`. The backend transcribes
  // it (Whisper) before the AI extraction pass runs (CLE-240).
  async function handleAudio(file: File) {
    setError(null);
    setBusy(true);
    setProcessingLabel(t('upload.processingTranscript'));
    try {
      await run(file, async (onProgress) => {
        const contentType = file.type || 'audio/mpeg';
        const { uploadUrl, key } = await requestMeetingNoteUploadUrl(owner, {
          fileName: file.name,
          contentType,
        });
        await uploadToSignedUrl(uploadUrl, file, contentType, onProgress);
        await ingestMutation.mutateAsync({
          audioKey: key,
          title: title.trim() || file.name,
          meetingType,
        });
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Încărcarea fișierului audio a eșuat.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submitText}
      className="mb-4 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4"
    >
      <p className="text-xs text-gray-500">
        Lipește textul întâlnirii, încarcă un PDF sau încarcă o înregistrare audio (transcrisă
        automat). AI-ul extrage titlul, data, participanții, rezumatul, deciziile, acțiunile de
        urmat, faptele cheie și întrebările deschise, apoi le indexează în baza de cunoștințe.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <input
          placeholder="Titlu (opțional — completat de AI)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none sm:col-span-2"
        />
        <Select
          label="Tip întâlnire"
          hideLabel
          value={meetingType}
          onChange={setMeetingType}
          options={MEETING_TYPES.map((t) => ({ value: t, label: meetingTypeLabel(t) }))}
        />
      </div>
      <textarea
        placeholder="Lipește notițele aici…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={5}
        className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-4">
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-brand-700 hover:underline">
            <Upload size={15} />
            Încarcă PDF
            <input
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handlePdf(file);
              }}
            />
          </label>
          <label className="inline-flex cursor-pointer items-center gap-1.5 text-sm text-brand-700 hover:underline">
            <Mic size={15} />
            Încarcă audio
            <input
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleAudio(file);
              }}
            />
          </label>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onDone}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Anulează
          </button>
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {busy && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            )}
            Procesează
          </button>
        </div>
      </div>
      <UploadProgressList uploads={uploads} processingLabel={processingLabel || undefined} />

      {error && <p className="text-sm text-red-600">{error}</p>}
    </form>
  );
}
